import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { TRUST_CHAIN } from "./c1lib";
import { Section } from "./PublicLayout";
import JudgeTour from "./JudgeTour";
import { Button } from "../components/ui/button";
import { ArrowRight, ArrowDown, HandHeart, ClipboardCheck, ShieldCheck, BadgeCheck, XCircle, PlayCircle } from "lucide-react";

export default function Landing() {
  const [tour, setTour] = useState(false);

  return (
    <div>
      <JudgeTour open={tour} onOpenChange={setTour} />

      {/* Hero — positive positioning */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h1 className="font-serif text-5xl sm:text-6xl font-semibold tracking-tight text-slate-950 leading-[1.05] max-w-3xl">
            Trust Infrastructure for Temples and Devotees.
          </h1>
          <p className="text-lg text-slate-700 mt-6 max-w-2xl leading-relaxed">
            Pramaana connects a devotee's Sankalpa with a verified temple, temple-approved seva, transparent money flow,
            fulfilment evidence and authorised validation — creating one traceable Verified Record.
          </p>
          <p className="font-serif text-2xl text-slate-900 mt-6 italic">One Sankalpa. One Verified Record.</p>
          <div className="mt-6 inline-flex items-start gap-2.5 border-l-2 border-emerald-500 bg-emerald-50/60 pl-4 pr-5 py-3 rounded-sm" data-testid="traction-line">
            <BadgeCheck size={17} className="text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-800 leading-relaxed">
              <span className="font-medium">First live Pramaana record certified:</span> Kalabhairava Abhishekam, Hyderabad — 2 September 2026.
            </p>
          </div>
          <div className="mt-8">
            <Button onClick={() => setTour(true)} data-testid="start-tour"
              className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2 h-11 px-6">
              <PlayCircle size={17} /> Take the 3-minute tour
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-6 max-w-2xl leading-relaxed">
            Pramaana helps devotees find verified temples, choose temple-approved Pujas and make a Sankalpa. It makes the
            temple-declared amount and money flow transparent, then carries the journey beyond booking through fulfilment
            evidence, authorised validation and one traceable Verified Record. Pramaana verifies the trust chain surrounding
            a seva, not belief or religious outcome.
          </p>
        </div>
      </div>

      {/* Trust chain as a journey */}
      <Section title="The Trust Chain" subtitle="One traceable sequence. Every state means something — nothing is marked verified, validated or certified decoratively.">
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-stretch gap-2.5" data-testid="trust-chain">
          {TRUST_CHAIN.map((s, i) => (
            <Fragment key={s.key}>
              <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-4 py-3 md:min-w-[210px] md:flex-1">
                <span className="font-mono text-sm text-slate-300 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <s.icon size={18} strokeWidth={1.5} className="text-slate-700 shrink-0" />
                <div className="font-medium text-slate-800 text-sm leading-tight">{s.label}</div>
              </div>
              {i < TRUST_CHAIN.length - 1 && (
                <div className="flex items-center justify-center text-slate-300 md:px-0.5" aria-hidden="true">
                  <ArrowRight size={16} className="hidden md:block" />
                  <ArrowDown size={16} className="md:hidden" />
                </div>
              )}
            </Fragment>
          ))}
        </div>

        {/* three principles */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-3">
          {[
            ["Payment is not fulfilment.", "A successful payment proves payment only — never that a seva was performed."],
            ["Evidence is not truth.", "Uploaded evidence is submitted material. It is accepted only after an authorised validator reviews it."],
            ["Hash is not religious proof.", "SHA-256 establishes record and file integrity / tamper-evidence — not that a religious act occurred."],
          ].map(([t, d]) => (
            <div key={t} className="border-l-2 border-slate-900 bg-white p-5">
              <div className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Principle</div>
              <div className="font-serif text-xl text-slate-900 leading-snug">{t}</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Experience Pramaana — three real roles */}
      <div className="bg-white border-y border-slate-200">
        <Section title="Experience Pramaana" subtitle="Walk the trust chain from each authorised role.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="role-entry">
            <RoleCard to="/devotee" icon={HandHeart} title="Devotee" testid="role-devotee"
              desc="Create a Sankalpa with a verified demo temple, see the transparent money flow, and follow the record through verification." />
            <RoleCard to="/temple-manager" icon={ClipboardCheck} title="Temple Manager" testid="role-manager"
              desc="Accept the Sankalpa, record fulfilment, attach the temple receipt and evidence, and submit it for validation." />
            <RoleCard to="/validation" icon={ShieldCheck} title="Authorised Validator" testid="role-validator"
              desc="Review the Sankalpa, financial record, receipt and evidence — then decide whether the requirements for certification have been met." />
          </div>
        </Section>
      </div>

      {/* Two equal outcomes */}
      <Section>
        <p className="font-serif text-3xl text-slate-950 mb-2">Verification is not a badge. It is an outcome.</p>
        <p className="text-sm text-slate-600 mb-8 max-w-2xl">Pramaana can certify a complete trust chain — and refuse certification when it is incomplete. Both outcomes matter equally.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="outcome-cards">
          <OutcomeCard to="/records/PRM-DEMOCERT01" testid="shortcut-certified" icon={BadgeCheck}
            accent="emerald" heading="Certified"
            body="All required evidence was reviewed and accepted by the authorised validator."
            cta="View Certified Sankalpa" />
          <OutcomeCard to="/records/PRM-DEMOHOLD02" testid="shortcut-withheld" icon={XCircle}
            accent="slate" heading="Certification Withheld"
            body="Required evidence was incomplete or insufficient, so certification was refused — this is not an error state."
            cta="See why certification was withheld" />
        </div>
      </Section>
    </div>
  );
}

function RoleCard({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="group bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-400 transition-colors duration-150">
      <div className="flex items-center justify-between">
        <Icon size={22} strokeWidth={1.5} className="text-slate-800" />
        <ArrowRight size={16} className="text-slate-300 group-hover:text-slate-700 transition-colors" />
      </div>
      <div className="font-serif text-2xl text-slate-950 mt-4">{title}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
    </Link>
  );
}

function OutcomeCard({ to, icon: Icon, heading, body, cta, accent, testid }) {
  const head = accent === "emerald" ? "border-emerald-200 bg-emerald-50" : "border-slate-300 bg-slate-100";
  const ic = accent === "emerald" ? "text-emerald-600" : "text-slate-600";
  return (
    <Link to={to} data-testid={testid}
      className="group border border-slate-200 rounded-sm bg-white overflow-hidden hover:border-slate-400 transition-colors duration-150 flex flex-col">
      <div className={`flex items-center gap-2 border-b px-5 py-3 ${head}`}>
        <Icon size={18} className={ic} />
        <span className="font-mono text-xs uppercase tracking-[0.14em] text-slate-800">{heading}</span>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-sm text-slate-700 leading-relaxed">{body}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-900 font-medium">
          {cta} <ArrowRight size={15} className="text-slate-400 group-hover:text-slate-800 transition-colors" />
        </span>
      </div>
    </Link>
  );
}
